<template>
  <div>
    <v-row class="login-background">
      <v-col cols="2" lg="2" md="1" sm="1"></v-col>
      <v-col cols="8" lg="8" md="10" sm="10" class="d-flex align-center justify-center">
        <v-card v-if="mode" class="mr-2 ml-2 card-login otp-card">
          <div
            class="mt-4 ml-3 mr-3 font-bold text-2xl"
            :class="$vuetify.theme.dark ? 'textwhite--text' : 'textblack--text'"
          >
            {{ mode === 'register' ? $t('msg.otp.registerTitle') : $t('msg.otp.verifyTitle') }}
          </div>
          <div class="mt-4 ml-3 mr-3 text-base textcolor4--text">
            {{ mode === 'register' ? $t('msg.otp.registerGuide') : $t('msg.otp.verifyGuide') }}
          </div>

          <template v-if="mode === 'register'">
            <div class="mt-6 text-center">
              <img :src="qrDataUri" alt="QR code" width="140" height="140" class="otp-qr" />
              <div class="text-sm textcolor4--text">{{ accountName }}</div>
            </div>
            <div class="mt-2 text-center">
              <a class="textcolor4--text text-sm" @click="showManualKey = !showManualKey">
                {{ $t('msg.otp.cantScan') }}
              </a>
              <div v-if="showManualKey" class="mt-2">
                <div class="text-sm textcolor4--text">{{ $t('msg.otp.manualKeyGuide') }}</div>
                <code class="otp-manual-key">{{ formattedManualKey }}</code>
              </div>
            </div>
          </template>

          <div class="mt-6 ml-3 mr-3">
            <otp-code-input
              ref="codeInput"
              :error="hasError"
              :disabled="submitting"
              @input="onCodeInput"
              @complete="submit"
              @submit="submit"
            />
          </div>
          <div v-if="errorMessage" class="mt-3 ml-3 mr-3 text-center text-sm otp-error-message">
            {{ errorMessage }}
          </div>
          <div class="mt-6 pl-3 pr-3 mb-4">
            <v-btn
              block
              x-large
              :color="$vuetify.theme.dark ? '#2D7ACA' : '#3DB6F3'"
              :loading="submitting"
              :disabled="submitting"
              class="sign-in"
              @click="submit(code)"
            >
              {{ $t('msg.otp.verify') }}
            </v-btn>
          </div>
        </v-card>
      </v-col>
      <v-col cols="2" lg="2" md="1" sm="1"></v-col>
    </v-row>
    <v-row class="login-footer">
      <v-col cols="2" lg="2" md="1"></v-col>
      <v-col cols="8" lg="8" md="10">
        <div class="pt-5">
          <span
            class="text-sm leading-tight"
            :style="$vuetify.theme.dark ? 'color: #54565F;' : 'color: #C1C7D0'"
          >
            Copyright © {{ new Date().getFullYear() }} Cyberlogitec Co.Ltd. All rights reserved
          </span>
        </div>
      </v-col>
      <v-col cols="2" lg="2" md="1"></v-col>
    </v-row>
    <dialogWindow
      ref="RestartDialog"
      info=""
      size="max"
      color="warning"
      :detail="restartMessage"
      @select-ok="goLogin()"
    >
    </dialogWindow>
  </div>
</template>

<script>
import dialogWindow from '@/vue-dlb/components/NewDialog.vue';
import OtpCodeInput from '@/vue-caas/components/OtpCodeInput.vue';
import loginCompleteMixin from '@/vue-caas/utils/login-complete-mixin.js';
import { hideProgress } from '@/vue-dlb/utils/sys-util.js';

/**
 * OTP 2단계 화면 (등록 / 인증). 비밀번호만 통과한 대기 상태이며 로그인 완료로 취급하지 않는다.
 * qrDataUri, manualKey 는 이 컴포넌트 data 에서만 표시하고 store / localStorage / 쿠키 / 로그에 남기지 않는다.
 */
export default {
  components: {
    dialogWindow,
    OtpCodeInput,
  },

  mixins: [loginCompleteMixin],

  data: () => ({
    mode: '',
    qrDataUri: '',
    manualKey: '',
    accountName: '',
    showManualKey: false,
    code: '',
    submitting: false,
    hasError: false,
    errorMessage: '',
    restartMessage: '',
  }),

  computed: {
    formattedManualKey() {
      return (this.manualKey.match(/.{1,4}/g) || []).join(' ');
    },
  },

  mounted() {
    hideProgress();
    this.loadSetup();
  },

  beforeDestroy() {
    this.clearSecrets();
  },

  methods: {
    async loadSetup() {
      try {
        const res = await this.$store.dispatch('membership/otpSetup');
        if (!res || res.restart || !res.mode) {
          this.restart(res && res.message);
          return;
        }
        if (res.mode === 'register') {
          this.qrDataUri = res.qrDataUri;
          this.manualKey = res.manualKey;
          this.accountName = res.accountName;
        }
        this.mode = res.mode;
      } catch (err) {
        this.restart();
      }
    },

    onCodeInput(value) {
      this.code = value;
      if (value) {
        this.hasError = false;
        this.errorMessage = '';
      }
    },

    async submit(value) {
      if (this.submitting) return;
      const code = value || this.code;
      if (!/^\d{6}$/.test(code || '')) {
        this.showError(this.$t('msg.otp.codeRequired'));
        return;
      }

      this.submitting = true;
      try {
        const res = await this.$store.dispatch('membership/otpVerify', { code });
        if (res && res.success) {
          // 성공 후에는 버튼을 다시 활성화하지 않는다 (중복 제출 방지)
          this.clearSecrets();
          this.completeLogin();
          return;
        }
        if (!res || res.restart) {
          this.restart(res && res.message);
          return;
        }
        this.submitting = false;
        this.showError(res.message);
      } catch (err) {
        this.restart();
      }
    },

    showError(message) {
      this.hasError = true;
      this.errorMessage = message;
      this.code = '';
      if (this.$refs.codeInput) this.$refs.codeInput.clear();
    },

    restart(message) {
      this.clearSecrets();
      this.restartMessage = message || this.$t('msg.otp.restart');
      this.$refs.RestartDialog.dialog = true;
    },

    clearSecrets() {
      this.qrDataUri = '';
      this.manualKey = '';
    },

    goLogin() {
      this.$router.push('/login');
    },
  },
};
</script>

<style>
/* card-login 의 padding: 8% 는 부모(v-col) 너비 기준이라 와이드 모니터에서 내용이 좁아진다 -> 고정 px */
.card-login.otp-card.v-card {
  width: 100%;
  max-width: 520px;
  padding: 40px 32px;
}
@media (max-width: 600px) {
  .card-login.otp-card.v-card {
    padding: 28px 16px;
  }
}
.otp-qr {
  display: inline-block;
  background: #ffffff;
  padding: 4px;
}
.otp-manual-key {
  display: inline-block;
  margin-top: 4px;
  font-size: 16px;
  letter-spacing: 1px;
  user-select: all;
}
.otp-error-message {
  color: #e53935;
}
</style>
